import { supabaseClient } from "../../services/supabase";

import type {
  Comanda,
  ComandaItem,
  EstoqueProduto,
  NewComanda,
} from "./types";

type AddItemResult = {
  item: ComandaItem;
  total: number;
};

type ChangeItemResult = {
  removed: boolean;
  item?: ComandaItem;
  item_id?: string;
  total: number;
};

export type FormaPagamento =
  | "pix"
  | "dinheiro"
  | "debito"
  | "credito";

export type PagamentoComanda = {
  forma_pagamento: FormaPagamento;
  valor: number;
};

export type CloseComandaResult = {
  success: boolean;

  comanda: Comanda;

  movimento_id?: string;

  movimento_ids: string[];

  total: number;

  forma_pagamento: FormaPagamento | "multiplo";

  pagamentos: PagamentoComanda[];
};

export type CancelComandaResult = {
  success: boolean;
  comanda: Comanda;
  total: number;
};

export type FinalizarSaidaInternaResult = {
  success: boolean;
  comanda: Comanda;
  total_referencia: number;
};

export type ReopenComandaResult = {
  success: boolean;
  comanda: Comanda;
  movimentos_arquivados: number;
  valor_anterior: number;
};

export async function cancelEmptyComanda(
  comandaId: string,
): Promise<CancelComandaResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "cancelar_comanda_vazia",
      {
        p_comanda_id: comandaId,
      },
    );

  if (error) {
    throw error;
  }

  return data as CancelComandaResult;
}

export async function loadOpenComandas(): Promise<Comanda[]> {
  const { data, error } =
    await supabaseClient
      .from("comandas")
      .select("*")
      .eq("status", "aberta")
      .order("created_at", {
        ascending: false,
      });

  if (error) {
    throw error;
  }

  return (data ?? []) as Comanda[];
}

export async function loadClosedCashDates(): Promise<string[]> {
  const { data, error } =
    await supabaseClient.rpc(
      "listar_dias_caixa_fechados",
    );

  if (error) {
    throw error;
  }

  return (data ?? []).map(
    (item: { data: string }) => item.data,
  );
}

export async function createComanda(
  input: NewComanda,
): Promise<Comanda> {
  const { data, error } =
    await supabaseClient
      .from("comandas")
      .insert({
        cliente_id:
          input.cliente_id ?? null,

        cliente_nome:
          input.cliente_nome.trim(),

        created_by:
          input.created_by ?? null,

        status: "aberta",

        total: 0,

        tipo:
          input.tipo ?? "venda",

        motivo_saida:
          input.motivo_saida ?? null,

        observacao_saida:
          input.observacao_saida?.trim() || null,
      })
      .select("*")
      .single();

  if (error) {
    throw error;
  }

  return data as Comanda;
}

export async function loadEstoqueProdutos(): Promise<
  EstoqueProduto[]
> {
  const { data, error } =
    await supabaseClient
      .from("estoque")
      .select("*")
      .eq("ativo", true)
      .order("nome");

  if (error) {
    throw error;
  }

  return (data ?? []) as EstoqueProduto[];
}

export async function loadComandaItems(
  comandaId: string,
): Promise<ComandaItem[]> {
  const { data, error } =
    await supabaseClient
      .from("comanda_itens")
      .select("*")
      .eq("comanda_id", comandaId)
      .order("created_at");

  if (error) {
    throw error;
  }

  return (data ?? []) as ComandaItem[];
}

export async function addItemToComanda(
  comandaId: string,
  produtoId: string,
): Promise<AddItemResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "comanda_adicionar_produto",
      {
        p_comanda_id: comandaId,
        p_produto_id: produtoId,
      },
    );

  if (error) {
    throw error;
  }

  return data as AddItemResult;
}

export async function increaseComandaItem(
  itemId: string,
): Promise<AddItemResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "comanda_aumentar_item",
      {
        p_item_id: itemId,
      },
    );

  if (error) {
    throw error;
  }

  return data as AddItemResult;
}

export async function decreaseComandaItem(
  itemId: string,
): Promise<ChangeItemResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "comanda_diminuir_item",
      {
        p_item_id: itemId,
      },
    );

  if (error) {
    throw error;
  }

  return data as ChangeItemResult;
}

export async function removeComandaItem(
  itemId: string,
): Promise<ChangeItemResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "comanda_remover_item",
      {
        p_item_id: itemId,
      },
    );

  if (error) {
    throw error;
  }

  return data as ChangeItemResult;
}

export async function closeComanda(
  comandaId: string,
  pagamentos: PagamentoComanda[],
): Promise<CloseComandaResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "fechar_comanda_multiplos",
      {
        p_comanda_id: comandaId,
        p_pagamentos: pagamentos,
      },
    );

  if (error) {
    throw error;
  }

  return data as CloseComandaResult;
}

export async function loadClosedComandas(
  dataInicio: string,
  dataFim: string,
): Promise<Comanda[]> {
  // Os campos de data representam dias no fuso local do navegador.
  // Convertemos os limites para ISO/UTC antes de consultar o timestamptz
  // do Supabase, evitando que vendas noturnas caiam no dia UTC seguinte.
  const inicio = new Date(
    `${dataInicio}T00:00:00`,
  ).toISOString();

  const fim = new Date(
    `${dataFim}T23:59:59.999`,
  ).toISOString();

  const { data, error } =
    await supabaseClient
      .from("comandas")
      .select("*")
      .eq("status", "fechada")
      .eq("tipo", "venda")
      .gte("closed_at", inicio)
      .lte("closed_at", fim)
      .order("closed_at", {
        ascending: false,
      })
      .limit(300);

  if (error) {
    throw error;
  }

  return (data ?? []) as Comanda[];
}

export async function reopenComanda(
  comandaId: string,
  motivo: string,
): Promise<ReopenComandaResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "reabrir_comanda",
      {
        p_comanda_id: comandaId,
        p_motivo: motivo.trim(),
      },
    );

  if (error) {
    throw error;
  }

  return data as ReopenComandaResult;
}

export async function finalizarSaidaInterna(
  comandaId: string,
): Promise<FinalizarSaidaInternaResult> {
  const { data, error } =
    await supabaseClient.rpc(
      "finalizar_saida_interna",
      {
        p_comanda_id: comandaId,
      },
    );

  if (error) {
    throw error;
  }

  return data as FinalizarSaidaInternaResult;
}
